import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Store, User, Lock, ArrowRight } from 'lucide-react';
import InputField from '../components/common/InputField';
import SelectField from '../components/common/SelectField';
import Button from '../components/common/Button';
import { BASE_URL_PREFIX } from '../api/api';
import { getShopsForGroup, isValidGroup, DEFAULT_GROUP_KEY } from '../config/shopGroups';

const LoginPage = ({ onLoginSuccess }) => {
  const [searchParams] = useSearchParams();
  const [shopCode, setShopCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Determine active shop group from query param or localStorage
  const activeGroupKey = useMemo(() => {
    const queryShop = searchParams.get('shop');
    if (queryShop) {
      const trimmed = queryShop.trim().toLowerCase();
      if (isValidGroup(trimmed)) {
        localStorage.setItem('swiftSale_activeShopGroup', trimmed);
        return trimmed;
      }
      return DEFAULT_GROUP_KEY;
    }

    const savedGroup = localStorage.getItem('swiftSale_activeShopGroup');
    if (savedGroup && isValidGroup(savedGroup)) {
      return savedGroup;
    }

    return DEFAULT_GROUP_KEY;
  }, [searchParams]);

  // Retrieve shop branch options based on active group
  const shopOptions = useMemo(() => {
    return getShopsForGroup(activeGroupKey);
  }, [activeGroupKey]);

  // Reset shop selection if the available options change and current selection is no longer valid
  useEffect(() => {
    const isCurrentValid = shopOptions.some((opt) => opt.value === shopCode && opt.value !== '');
    if (!isCurrentValid) {
      setShopCode('');
    }
  }, [shopOptions, shopCode]);

  const handleLogin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!shopCode) {
      setError('Please select your shop/branch to proceed.');
      return;
    }
    if (!username.trim() || !password.trim()) {
      setError('Please enter your username and password.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const loginUrl = `${BASE_URL_PREFIX}/${shopCode}/api/oauth/token/manager`;
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'password',
          client_id: '97333572-5248-4bcb-9c99-be5da3616000',
          client_secret: 'EhMuojWFjgYUWKst3oZOULIMwfFdUF97zYij5NRy',
          username,
          password,
          scope: ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Authentication failed. Please check your credentials.');
      }

      const result = await response.json();
      
      if (result && result.status && result.data && result.data.accessToken) {
        onLoginSuccess(result.data.accessToken, shopCode);
      } else {
        setError('Invalid credentials or unexpected response from server.');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please check your network and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 relative overflow-hidden font-sans">
      {/* Background Decorative Rings */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md z-10">
        {/* Logo and Brand Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-3">
            <img
              src={`${process.env.PUBLIC_URL}/icon-192.png`}
              alt="SwiftSale Manager Icon"
              className="w-16 h-16 rounded-2xl shadow-xl shadow-emerald-500/20"
            />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">
            Swift<span className="text-emerald-400">Sale</span> Manager
          </h1>
        </div>

        {/* Card Container */}
        <div className="bg-white/95 dark:bg-[#1e293b]/95 backdrop-blur-xl border border-slate-200/80 dark:border-slate-700/80 rounded-2xl shadow-2xl p-6 sm:p-8">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Sign In
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Enter your credentials to access your store dashboard
            </p>
          </div>

          {error && (
            <div className="mb-5 p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-200 text-xs flex items-center gap-2.5">
              <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <SelectField
              label="Branch / Shop"
              value={shopCode}
              onChange={(e) => setShopCode(e.target.value)}
              options={shopOptions}
              name="shopCode"
              required
              icon={<Store className="w-4 h-4" />}
            />

            <InputField
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              name="username"
              required
              icon={<User className="w-4 h-4" />}
            />

            <InputField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              name="password"
              required
              icon={<Lock className="w-4 h-4" />}
            />

            <div className="pt-2">
              <Button
                type="submit"
                onClick={handleLogin}
                disabled={loading}
                className="w-full py-3 text-sm font-semibold rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-600/25 transition-all"
                icon={loading ? null : <ArrowRight className="w-4 h-4 ml-1" />}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
